/**
 * PATH: packages/email-hub/src/feature/components/MarkupToolbar.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-MARKUPTOOLBAR-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/MarkupToolbar.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/MarkupToolbar.tsx:
 *     thin re-export shim that points back here.
 */

import { ArrowUpRight, Eraser, MousePointer2, Pencil, Ruler, Square, Type, Undo2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { MARKUP_COLORS, type MarkupTool } from '@quotech/email-hub/feature/lib/markupCanvas'

export interface MarkupToolbarProps {
  tool: MarkupTool
  color: string
  onSelectTool: (tool: MarkupTool) => void
  onSelectColor: (color: string) => void
  onUndo: () => void
  onClear: () => void
}

export function MarkupToolbar({
  tool, color, onSelectTool, onSelectColor, onUndo, onClear,
}: MarkupToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-border shrink-0">
      <ToolBtn icon={MousePointer2} label="Select" active={tool === 'select'}   onClick={() => onSelectTool('select')} />
      <ToolBtn icon={ArrowUpRight}  label="Arrow"  active={tool === 'arrow'}    onClick={() => onSelectTool('arrow')} />
      <ToolBtn icon={Square}        label="Rect"   active={tool === 'rect'}     onClick={() => onSelectTool('rect')} />
      <ToolBtn icon={Ruler}         label="Measure" active={tool === 'measure'} onClick={() => onSelectTool('measure')} />
      <ToolBtn icon={Pencil}        label="Free"   active={tool === 'freehand'} onClick={() => onSelectTool('freehand')} />
      <ToolBtn icon={Type}          label="Text"   active={tool === 'text'}     onClick={() => onSelectTool('text')} />
      <span className="w-px h-5 bg-border mx-1" aria-hidden />
      {MARKUP_COLORS.map((c) => (
        <button
          key={c}
          onClick={() => onSelectColor(c)}
          className={cn(
            'h-6 w-6 rounded-full border-2 transition-transform',
            color === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105',
          )}
          style={{ backgroundColor: c }}
          aria-label={`Color ${c}`}
        />
      ))}
      <span className="w-px h-5 bg-border mx-1" aria-hidden />
      <ToolBtn icon={Undo2}  label="Undo"  onClick={onUndo} />
      <ToolBtn icon={Eraser} label="Clear" onClick={onClear} />
    </div>
  )
}

function ToolBtn({
  icon: Icon, label, active, onClick,
}: {
  icon: LucideIcon; label: string; active?: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium border border-transparent',
        active ? 'bg-accent border-border text-foreground' : 'text-muted-foreground hover:bg-accent',
      )}
      title={label}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}
